import type { LDFlagSet } from "launchdarkly-react-client-sdk";

/**
 * Flag overrides for headless (E2E) runs.
 *
 * LaunchDarkly is deliberately never initialised under a headless browser —
 * there is no context to identify with, so `useFlags()` returns `{}` and every
 * flag reads falsy. That makes any surface behind a release flag unreachable
 * from the E2E suite, however the flag is targeted in LaunchDarkly itself.
 *
 * Tests opt individual flags on by seeding them before the app boots, either in
 * `localStorage` under {@link HEADLESS_FLAG_OVERRIDES_KEY}:
 *
 *     localStorage.setItem(
 *       "compono.e2e.flags",
 *       JSON.stringify({ "hire-job-requisitions": true }),
 *     );
 *
 * or as a `?ldFlags=` query parameter listing the flags to switch on:
 *
 *     /?ldFlags=hire-job-requisitions,some-other-flag
 *
 * Keys are the original LaunchDarkly keys; the caller camel-cases them to match
 * what `useFlags()` would return.
 *
 * Reading these is gated on `HEADLESS_TESTING`, so nothing here runs in an
 * ordinary browser. Note that the gate is user-agent based and therefore
 * spoofable: treat this as a way to change what the *client* renders, never as
 * an authorisation boundary. Every flagged surface is still authorised
 * server-side, so switching a flag on here cannot grant a user access they do
 * not already have.
 */
export const HEADLESS_FLAG_OVERRIDES_KEY = "compono.e2e.flags";

const fromQueryString = (search: string): LDFlagSet => {
  const raw = new URLSearchParams(search).get("ldFlags");
  if (!raw) return {};

  return Object.fromEntries(
    raw
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean)
      .map((key) => [key, true]),
  );
};

const fromLocalStorage = (): LDFlagSet => {
  try {
    const raw = window.localStorage.getItem(HEADLESS_FLAG_OVERRIDES_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return parsed as LDFlagSet;
  } catch {
    // Unparseable JSON, or storage blocked entirely — either way there is
    // nothing to override, and a test harness problem must not break the app.
    return {};
  }
};

/**
 * The flags a headless run has asked for, keyed as LaunchDarkly knows them.
 * Query-string entries win over stored ones, so a single URL can override a
 * value that a previous test left behind.
 */
export const readHeadlessFlagOverrides = (): LDFlagSet => ({
  ...fromLocalStorage(),
  ...fromQueryString(window.location.search),
});
